import React, { useEffect, useRef, useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import { ExternalLink, Github, Globe, Bot, MapPin, Sparkles } from 'lucide-react';

const Projects = () => {
  const [isVisible, setIsVisible] = useState(false);
  const [hoveredProject, setHoveredProject] = useState(null);
  const sectionRef = useRef(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setIsVisible(true);
        }
      },
      { threshold: 0.1 }
    );

    if (sectionRef.current) {
      observer.observe(sectionRef.current);
    }

    return () => observer.disconnect();
  }, []);

  const projects = [
    {
      id: 1,
      title: "Smriti Samrakshana",
      subtitle: "Cultural Heritage Preservation Platform",
      description: "An interactive platform for preserving and showcasing cultural heritage through technology. Features geographic visualizations and responsive design to make cultural information accessible to everyone.",
      longDescription: "This platform addresses the critical need for cultural preservation in the digital age. Using modern web technologies and interactive maps, it creates an engaging experience for users to explore and learn about cultural heritage sites and traditions.",
      image: "https://media.istockphoto.com/id/951823988/photo/india.jpg?s=2048x2048&w=is&k=20&c=g15WKh5sir-XGCZ3kP_FWZrfmQTe7cY6DPpmxinZ99g=",
      technologies: ["HTML", "CSS", "JavaScript", "Google GeoChart API"],
      features: ["Interactive geographic visualizations", "Responsive design", "Cultural site mapping", "Heritage information system"],
      demoUrl: "#",
      codeUrl: "#",
      status: "Completed",
      icon: MapPin,
      gradient: "from-orange-500 to-red-500"
    },
    {
      id: 2,
      title: "Career Guidance Chatbot",
      subtitle: "AI-Powered Advisory Tool",
      description: "An intelligent chatbot that provides personalized career guidance using natural language processing. Helps students and professionals make informed career decisions through AI-powered conversations.",
      longDescription: "Leveraging the power of OpenAI's API and Streamlit, this chatbot offers personalized career advice, skill recommendations, and educational pathways. It's designed to democratize access to quality career guidance.",
      image: "https://media.istockphoto.com/id/2199342030/photo/person-using-laptop-with-ai-content-generator-interaction-with-the-ai-assistant-to-solve-some.jpg?s=2048x2048&w=is&k=20&c=R4VpTAq4yu3ieq8_9u4BEdgdGCguH-6epjpwcpIFTns=",
      technologies: ["Python", "Streamlit", "OpenAI API", "NLP"],
      features: ["Natural language processing", "Personalized guidance", "Career path recommendations", "Interactive chat interface"],
      demoUrl: "https://career-guidance-chatbot-shivendu0103.streamlit.app/",
      codeUrl: "https://github.com/Shivendu0103/Career-Guidance-ChatBot",
      status: "Completed",
      icon: Bot,
      gradient: "from-blue-500 to-purple-500"
    }
  ];

  const handleProjectClick = (url, type) => {
    if (url === "#") {
      alert(`${type === 'demo' ? 'Live Demo' : 'Source Code'} will be available soon!`);
    } else {
      window.open(url, '_blank');
    }
  };

  return (
    <section id="projects" ref={sectionRef} className="py-20 bg-slate-50 dark:bg-slate-800 transition-colors duration-300">
      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Section Header */}
        <div className={`text-center mb-16 transition-all duration-1000 ${isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'}`}>
          <h2 className="text-4xl lg:text-5xl font-bold text-slate-900 dark:text-white mb-4">
            Featured <span className="bg-gradient-to-r from-blue-600 to-cyan-500 bg-clip-text text-transparent">Projects</span>
          </h2>
          <p className="text-xl text-slate-600 dark:text-slate-300 max-w-3xl mx-auto">
            Showcasing my passion for creating meaningful solutions that solve real-world problems
          </p>
        </div>

        {/* Projects Grid */}
        <div className="grid lg:grid-cols-2 gap-8">
          {projects.map((project, index) => (
            <Card 
              key={project.id}
              className={`group overflow-hidden hover:shadow-2xl transition-all duration-500 transform hover:scale-[1.02] ${
                isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'
              }`}
              style={{ transitionDelay: `${index * 200}ms` }}
              onMouseEnter={() => setHoveredProject(project.id)}
              onMouseLeave={() => setHoveredProject(null)}
            >
              {/* Project Image */}
              <div className="relative h-64 overflow-hidden">
                <img 
                  src={project.image}
                  alt={project.title}
                  className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-110"
                />
                
                {/* Overlay */}
                <div className={`absolute inset-0 bg-gradient-to-t ${project.gradient} opacity-0 group-hover:opacity-80 transition-opacity duration-300`}>
                  <div className="absolute inset-0 flex items-center justify-center space-x-4">
                    <Button
                      onClick={() => handleProjectClick(project.demoUrl, 'demo')}
                      size="sm"
                      className="bg-white/20 backdrop-blur-sm text-white border-white/30 hover:bg-white/30 transition-all duration-200"
                    >
                      <ExternalLink className="w-4 h-4 mr-2" />
                      Live Demo
                    </Button>
                    <Button
                      onClick={() => handleProjectClick(project.codeUrl, 'code')}
                      variant="outline"
                      size="sm"
                      className="bg-white/20 backdrop-blur-sm text-white border-white/30 hover:bg-white/30 transition-all duration-200"
                    >
                      <Github className="w-4 h-4 mr-2" />
                      Source Code
                    </Button>
                  </div>
                </div>

                {/* Status Badge */}
                <div className="absolute top-4 right-4">
                  <Badge className="bg-green-500 text-white">
                    {project.status}
                  </Badge>
                </div>

                {/* Project Icon */}
                <div className="absolute top-4 left-4">
                  <div className={`p-2 rounded-lg bg-gradient-to-r ${project.gradient} text-white`}>
                    <project.icon className="w-5 h-5" />
                  </div>
                </div>
              </div>

              {/* Project Content */}
              <CardHeader className="pb-4">
                <CardTitle className="flex items-center justify-between">
                  <div>
                    <h3 className="text-xl font-bold text-slate-900 dark:text-white group-hover:text-blue-600 dark:group-hover:text-cyan-400 transition-colors duration-200">
                      {project.title}
                    </h3>
                    <p className="text-sm text-slate-500 dark:text-slate-400 font-normal">
                      {project.subtitle}
                    </p>
                  </div>
                  <Sparkles className="w-5 h-5 text-yellow-500 opacity-0 group-hover:opacity-100 transition-opacity duration-300" />
                </CardTitle>
              </CardHeader>

              <CardContent className="space-y-4">
                {/* Description */}
                <p className="text-slate-600 dark:text-slate-300 leading-relaxed">
                  {hoveredProject === project.id ? project.longDescription : project.description}
                </p>

                {/* Technologies */}
                <div className="flex flex-wrap gap-2">
                  {project.technologies.map((tech, techIndex) => (
                    <Badge 
                      key={techIndex}
                      variant="secondary"
                      className="text-xs bg-blue-100 dark:bg-blue-900/30 text-blue-700 dark:text-blue-300 hover:scale-105 transition-transform duration-200"
                    >
                      {tech}
                    </Badge>
                  ))}
                </div>

                {/* Key Features */}
                <div className="space-y-2">
                  <h4 className="text-sm font-semibold text-slate-900 dark:text-white">Key Features:</h4>
                  <ul className="text-sm text-slate-600 dark:text-slate-300 space-y-1">
                    {project.features.slice(0, 3).map((feature, featureIndex) => (
                      <li key={featureIndex} className="flex items-start">
                        <span className="text-blue-500 mr-2">•</span>
                        {feature}
                      </li>
                    ))}
                  </ul>
                </div>

                {/* Action Buttons */}
                <div className="flex space-x-3 pt-4">
                  <Button
                    onClick={() => handleProjectClick(project.demoUrl, 'demo')}
                    size="sm"
                    className="flex-1 bg-blue-600 hover:bg-blue-700 text-white"
                  >
                    <Globe className="w-4 h-4 mr-2" />
                    View Demo
                  </Button>
                  <Button
                    onClick={() => handleProjectClick(project.codeUrl, 'code')}
                    variant="outline"
                    size="sm"
                    className="flex-1"
                  >
                    <Github className="w-4 h-4 mr-2" />
                    View Code
                  </Button>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>

        {/* More Projects CTA */}
        <div className={`text-center mt-12 transition-all duration-1000 delay-400 ${isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'}`}>
          <p className="text-slate-600 dark:text-slate-300 mb-6">
            More exciting projects are coming soon! Check back regularly for updates.
          </p>
          <Button
            onClick={() => window.open('https://github.com/shivendu0103', '_blank')}
            variant="outline"
            size="lg"
            className="px-8 py-3 rounded-full border-2 border-blue-600 text-blue-600 hover:bg-blue-600 hover:text-white transition-all duration-300 hover:scale-105"
          >
            <Github className="w-5 h-5 mr-2" />
            View All on GitHub
          </Button>
        </div>
      </div>
    </section>
  );
};

export default Projects;